import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

export interface ConfirmationStep {
  id: string;
  label: string;
  shortLabel: string;
  genieIcon: GenieIconName;
  required: boolean;
  description: string;
}

export const CONFIRMATION_STEPS: ConfirmationStep[] = [
  {
    id: "branding",
    label: "Branding & Appearance",
    shortLabel: "Branding",
    genieIcon: "play",
    required: true,
    description: "Avatar and visual identity",
  },
  {
    id: "identity",
    label: "Identity & Purpose",
    shortLabel: "Identity",
    genieIcon: "brain",
    required: true,
    description: "Name, category, and use case",
  },
  {
    id: "conversation",
    label: "Voice & Conversation",
    shortLabel: "Voice",
    genieIcon: "settings",
    required: true,
    description: "Voice selection and conversation style",
  },
  {
    id: "insights",
    label: "Insights & Outcomes",
    shortLabel: "Insights",
    genieIcon: "lightbulb",
    required: false,
    description: "Collection insights and notifications",
  },
  {
    id: "knowledge",
    label: "Knowledge Base",
    shortLabel: "Knowledge",
    genieIcon: "book-open",
    required: false,
    description: "Documents and resources",
  },
  {
    id: "advanced",
    label: "Advanced Settings",
    shortLabel: "Advanced",
    genieIcon: "palette",
    required: false,
    description: "Technical configuration",
  },
  {
    id: "ownership",
    label: "Ownership",
    shortLabel: "Ownership",
    genieIcon: "user-check",
    required: false,
    description: "Choose who owns this genie",
  },
  {
    id: "review",
    label: "Review & Create",
    shortLabel: "Review",
    genieIcon: "chat-check",
    required: true,
    description: "Final review before creation",
  },
];

interface StepNavigationProps {
  currentStep: string;
  completedSteps: string[];
  onStepChange: (stepId: string) => void;
}

/**
 * How far forward the stepper lets you jump.
 *
 * You can always go back, and always reach the step you are on. Beyond that you
 * may skip over a run of optional steps — Insights, Knowledge, Advanced,
 * Ownership — because none of them blocks and pretending otherwise just makes
 * people click Next four times. The first `required` step ahead of you is the
 * wall, and it stays a wall.
 */
const furthestReachableIndex = (fromIndex: number): number => {
  let index = fromIndex;
  while (index + 1 < CONFIRMATION_STEPS.length && !CONFIRMATION_STEPS[index + 1].required) {
    index += 1;
  }
  return index;
};

export const StepNavigation = ({ currentStep, completedSteps, onStepChange }: StepNavigationProps) => {
  const currentStepIndex = CONFIRMATION_STEPS.findIndex((s) => s.id === currentStep);
  const reachableIndex = furthestReachableIndex(currentStepIndex);

  return (
    <div className="mb-8">
      {/* Desktop: Horizontal stepper */}
      <div className="hidden md:flex items-center justify-between">
        {CONFIRMATION_STEPS.map((step, index) => {
          const isActive = currentStep === step.id;
          const isCompleted = completedSteps.includes(step.id);
          const isAccessible = index <= reachableIndex || isCompleted;

          return (
            <div key={step.id} className="flex items-center flex-1">
              {/* Step indicator */}
              <button
                onClick={() => isAccessible && onStepChange(step.id)}
                disabled={!isAccessible}
                className={cn(
                  "flex flex-col items-center gap-2 transition-all group",
                  isAccessible ? "cursor-pointer" : "cursor-not-allowed opacity-50",
                )}
              >
                <div className="relative">
                  <div
                    className={cn(
                      "w-12 h-12 rounded-full flex items-center justify-center transition-all",
                      isActive && "ring-4 scale-110",
                      !isCompleted && !isActive && "bg-gray-200",
                    )}
                    style={
                      isActive
                        ? {
                            background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)",
                            boxShadow: "0 0 0 4px rgba(78, 156, 255, 0.2)",
                          }
                        : isCompleted && !isActive
                          ? {
                              background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)",
                            }
                          : undefined
                    }
                  >
                    {isCompleted && !isActive ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <GenieIcon
                        name={step.genieIcon}
                        size={24}
                        color={isActive ? "#FFFFFF" : "#6B7280"}
                        gradientEnabled={false}
                        label={step.label}
                      />
                    )}
                  </div>
                  {step.required && !isCompleted && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#FF4DB8] rounded-full border-2 border-white" />
                  )}
                </div>
                <div className="text-center">
                  <p
                    className={cn(
                      "text-sm font-medium transition-colors",
                      isActive && "text-gray-900",
                      !isActive && "text-gray-600",
                    )}
                  >
                    {step.shortLabel}
                  </p>
                  {!step.required && !isCompleted && (
                    <p className="text-[11px] text-gray-400">Optional</p>
                  )}
                </div>
              </button>

              {/* Connector line */}
              {index < CONFIRMATION_STEPS.length - 1 && (
                <div className="flex-1 h-1 mx-2 relative">
                  <div className="absolute inset-0 bg-gray-200 rounded-full" />
                  <div
                    className="absolute inset-0 rounded-full transition-all duration-500"
                    style={{
                      width: isCompleted ? "100%" : "0%",
                      background: isCompleted ? "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" : "#E5E7EB",
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* Voice Widget Container - aligned with step circles */}
        <div id="voice-widget-container" className="flex items-center" />
      </div>

      {/* Mobile: Simple progress bar + current step */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <GenieIcon
              name={CONFIRMATION_STEPS[currentStepIndex].genieIcon}
              size={28}
              color="#4E9CFF"
              gradientEnabled={false}
              label={CONFIRMATION_STEPS[currentStepIndex].label}
            />
            <div>
              <p className="text-sm font-semibold text-gray-900">{CONFIRMATION_STEPS[currentStepIndex].label}</p>
              <p className="text-xs text-gray-500">
                Step {currentStepIndex + 1} of {CONFIRMATION_STEPS.length}
              </p>
            </div>
          </div>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${((currentStepIndex + 1) / CONFIRMATION_STEPS.length) * 100}%`,
              background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
            }}
          />
        </div>
      </div>
    </div>
  );
};
